import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
import SSRSuspense from "@dogstrust/dt-web-core-lib-front-end/src/components/SSRSuspense/SSRSuspense";
import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import IntroHomepageComponent, {
	type ContentVariant,
	type MediaVariant,
} from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroHomepageComponent/IntroHomepageComponent";
import ParagraphSections from "@dogstrust/dt-web-core-lib-front-end/src/containers/paragraphs/ParagraphSections/ParagraphSections";
import { usePageManager } from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/uk/components/Layout/Layout";

import { mapQuickLinksUnionToQuickLinksProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/mappers";
import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";

import React, { lazy, useEffect, useState } from "react";

const FilterPanel = lazy(
	() =>
		import(
			"@dogstrust/dt-web-core-lib-front-end/src/containers/filters/FilterPanel"
		),
);

interface HomePageHeadProps {
	data: Queries.HomePageQuery;
}
export const Head: React.FC<HomePageHeadProps> = ({
	data: {
		homePages: { nodes: homePageNodes },
	},
}) => {
	const [homePageNode, setHomepageNode] = useState(
		homePageNodes.find(
			(node: {
				field_ab_testing_key: string;
			}) => node.field_ab_testing_key === "default",
		) || homePageNodes[homePageNodes.length - 1],
	);
	useEffect(() => {
		const updateHomepageNode = () => {
			if (window.homepageABVariant) {
				const abVariant =
					homePageNodes.find(
						(node: {
							field_ab_testing_key: string;
						}) => node.field_ab_testing_key === window.homepageABVariant,
					) || homePageNodes[homePageNodes.length - 1];

				setHomepageNode(abVariant);
			}
		};
		updateHomepageNode();
	}, [homePageNodes]);
	const schema = {
		"@context": "https://schema.org",
		"@type": "NGO",
		name: "Dogs Trust",
		description: homePageNode?.field_short_description,
		url: process.env.GATSBY_DOMAIN_NAME,
		logo: "",
		sameAs: [
			"https://en.wikipedia.org/wiki/Dogs_Trust",
			"https://twitter.com/DogsTrust",
			"https://www.youtube.com/user/DogsTrust",
			"https://www.facebook.com/DogsTrust",
			"https://www.instagram.com/dogstrust",
		],
	};
	return (
		<DefaultHead pageUrl="/" hrefLangUrl={"https://www.dogstrust.ie"}>
			<SEO
				{...getSeoProps(homePageNode?.field_meta_tags, {
					title: "Dogs Trust",
					description: homePageNode?.field_short_description,
					canonical_url: process.env.GATSBY_DOMAIN_NAME,
					og_type: "article",
					og_url: process.env.GATSBY_DOMAIN_NAME,
					og_title: "Dogs Trust",
					og_description: homePageNode?.field_short_description,
					twitter_cards_type: "summary",
					twitter_cards_description: homePageNode?.field_short_description,
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: "Dogs Trust",
					twitter_cards_page_url: process.env.GATSBY_DOMAIN_NAME,
					schemaMarkup: schema,
				})}
			/>
		</DefaultHead>
	);
};
interface HomePageProps {
	data: Queries.HomePageDataFragment;
	allCentres: Queries.RehomingCentreDataFragment[];
}

const HomePage: React.FC<HomePageProps> = ({
	data: {
		title,
		field_media,
		field_content,
		field_short_description,
		field_cta,
		field_embed_code,
		field_widget_theme = "primary",
		relationships: {
			field_featured_image,
			field_featured_video,
			field_section_to_display,
			field_quick_links,
		},
	},
	allCentres,
}) => {
	const { setContentWidth, deregisterPage, usePagination } = usePageManager();
	useEffect(() => {
		usePagination(false);
		setContentWidth("DEFAULT");
		return () => deregisterPage();
	}, [setContentWidth, deregisterPage, usePagination]);

	return (
		<>
			<Layout breadcrumbs={[]}>
				<IntroHomepageComponent
					title={title}
					text={field_short_description}
					ctaTitle={field_cta?.title}
					ctaUrl={field_cta?.uri}
					imageSrc={field_featured_image?.field_media_image.uri}
					imageAlt={field_featured_image?.field_media_image.alt}
					widgetTheme={field_widget_theme}
					embedCode={field_embed_code as string}
					videoId={field_featured_video?.field_video_id as string}
					contentVariant={field_content as ContentVariant}
					mediaVariant={field_media as MediaVariant}
					linkItems={field_quick_links?.map(
						mapQuickLinksUnionToQuickLinksProps,
					)}
				/>
				{field_section_to_display.map(
					(section: Queries.ParagraphSectionsDataFragment, i: number) => (
						<ParagraphSections
							key={"id" in section ? section.id : i}
							data={section}
							thisSectionKey={i}
							isHomePage={true}
						/>
					),
				)}
			</Layout>
			<SSRSuspense>
				<FilterPanel allCentres={allCentres} isListingPage={false} />
			</SSRSuspense>
		</>
	);
};
export default HomePage;
